import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, CartesianGrid, LineChart, Line } from 'recharts';
import axios from 'axios';
import { LockClosedIcon, ArrowLeftIcon, ArrowsUpDownIcon, InformationCircleIcon, CalendarIcon } from '@heroicons/react/24/solid';
import DisclaimerFooter from './DisclaimerFooter';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Chart from 'chart.js/auto';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerStyles.css';
import { useAuth } from '../context/AuthContext';
import PdfExportModal from './PdfExportModal';
import { addPdfFinalText, addPdfPageNumbers, drawPdfHeader, getWhitelabelSettings, hexToRgb, loadImageFromUrl } from '../utils/whitelabelPdf';
import { signalDescriptions } from '../constants/signalDescriptions';
import { parseLocalDate } from '../utils/dates';

const methodologyText = `
Our investment methodology combines alternative data and enhanced quantitative factors to deliver differentiated portfolios. We go beyond traditional financial metrics by integrating unique signals such as analyst estimate quality, earnings call sentiment, and innovation capacity. Our quantitative models are designed to dynamically adjust for sector and accounting differences, focusing on forward-looking value, quality, momentum, and volatility. This disciplined approach aims to identify mispriced opportunities and manage risk effectively, providing a robust foundation for long-term outperformance.

Alternative Data Signals:
• Earnings Expectations: We analyze the quality and trends of analyst estimates, incorporating peer momentum and company guidance to generate proprietary forecasts.
• Earnings Calls Sentiment: Using advanced NLP, we extract nuanced sentiment and forward-looking statements from earnings call transcripts, providing insights beyond simple keyword analysis.
• Innovation Signal: We assess a company's innovation potential by examining patent quality, citations, and the ability to attract specialized talent, identifying firms investing in disruptive R&D.

Enhanced Quantitative Factors:
• Value: Incorporates forward-looking earnings and sector adjustments for a dynamic measure of intrinsic value.
• Quality: Focuses on sustainable earnings, cash flow consistency, and balance sheet strength.
• Momentum: Uses peer benchmarking and economic linkages to capture robust, company-specific momentum.
• Volatility: Isolates the component of volatility predictive of positive returns, controlling for unintended factor exposures.

`;

const disclaimerText = `
SharpeMetrix and all of its content is for Investment Professional Use Only. SharpeMetrix is an Internet-based publication, not an investment advisor or Broker/Dealer. It is not registered as an investment adviser with any regulatory agency. The Site offers impersonal investment models without personalized advice. Past performance does not predict future results. All investments involve risk, including the potential loss of principal. Consult with a qualified financial advisor before making any investment decisions.
`;

// Simplified per-model methodology blurbs (keyed by portfolio_config model_name)
const MODEL_METHODOLOGY = {
  sig_sp500_30: 'Top 30 S&P 500 stocks by multi-factor signal. Max position 10%, sector ±7% of benchmark. Signal-tilted weights. Monthly rebalance.',
  sig_sp500_50: 'Top 50 S&P 500 stocks by multi-factor signal. Max position 10%, sector ±7% of benchmark. Signal-tilted weights. Monthly rebalance.',
  sig_r1000_30: 'Top 30 Russell 1000 stocks by multi-factor signal. Max position 10%, sector ±7% of benchmark. Signal-tilted weights. Monthly rebalance.',
  sig_r1000_50: 'Top 50 Russell 1000 stocks by multi-factor signal. Max position 10%, sector ±7% of benchmark. Signal-tilted weights. Monthly rebalance.',
  sig_r2000_30: 'Top 30 Russell 2000 (small-cap) stocks by multi-factor signal. Max position 10%, sector ±7% of benchmark. Signal-tilted weights. Monthly rebalance.',
  sig_r2000_50: 'Top 50 Russell 2000 (small-cap) stocks by multi-factor signal. Max position 10%, sector ±7% of benchmark. Signal-tilted weights. Monthly rebalance.',
  tb_sp500: 'Top 40 S&P 500 stocks by TrueBeats earnings signal. Max position 10%, sector ±7% of benchmark. Signal-tilted weights. Monthly rebalance.',
  value_r2000: 'Top 40 Russell 2000 stocks by value/quality signal. Max position 10%, sector ±7% of benchmark. Signal-tilted weights. Monthly rebalance.',
  momentum_r1000: 'Top 40 Russell 1000 stocks by momentum signal. Max position 10%, sector ±7% of benchmark. Signal-tilted weights. Monthly rebalance.',
  sig_indexEnh_sp500: 'S&P 500 index enhancement — tilts to high-signal stocks while staying near index weights. Max position 3%, sector ±7%, relative weight ±0.5%, turnover 20%. Monthly rebalance.',
  sig_longOpt_sp500_50: 'Optimized 50-stock S&P 500 portfolio. Max position 3%, sector ±7%, factor ±0.5σ, turnover 20%. Monthly rebalance.',
  sig_longSim_sp500_40: 'Top 40 S&P 500 stocks by multi-factor signal. Max position 10%, sector ±7% of benchmark. Signal-tilted weights. Monthly rebalance.',
  sig_r2000_top_100: 'Top 100 Russell 2000 stocks by multi-factor signal. Max position 3%, sector ±7% of benchmark. Signal-tilted weights. Monthly rebalance.',
  powerlist_r2000_100: 'Top 100 Russell 2000 stocks by multi-factor signal, equal-weighted (~1% each). Relaxed constraints for a pure ranking book. Monthly rebalance.',
};

// Construction params from portfolio_config.json
const MODEL_CONSTRUCTION = {
  sig_sp500_30: { method: 'long_simple', signal: 'Multi-factor', benchmark: 'S&P 500', nstock: 30, max_pos: 10, bnd_sec: 7, bnd_adv: 3, bnd_turnover: 20, bnd_factor: 50, bnd_rel_weight: 0.5, soft_bnd: true },
  sig_sp500_50: { method: 'long_simple', signal: 'Multi-factor', benchmark: 'S&P 500', nstock: 50, max_pos: 10, bnd_sec: 7, bnd_adv: 3, bnd_turnover: 20, bnd_factor: 50, bnd_rel_weight: 0.5, soft_bnd: true },
  sig_r1000_30: { method: 'long_simple', signal: 'Multi-factor', benchmark: 'Russell 1000', nstock: 30, max_pos: 10, bnd_sec: 7, bnd_adv: 3, bnd_turnover: 20, bnd_factor: 50, bnd_rel_weight: 0.5, soft_bnd: true },
  sig_r1000_50: { method: 'long_simple', signal: 'Multi-factor', benchmark: 'Russell 1000', nstock: 50, max_pos: 10, bnd_sec: 7, bnd_adv: 3, bnd_turnover: 20, bnd_factor: 50, bnd_rel_weight: 0.5, soft_bnd: true },
  sig_r2000_30: { method: 'long_simple', signal: 'Multi-factor', benchmark: 'Russell 2000', nstock: 30, max_pos: 10, bnd_sec: 7, bnd_adv: 3, bnd_turnover: 20, bnd_factor: 50, bnd_rel_weight: 0.5, soft_bnd: true },
  sig_r2000_50: { method: 'long_simple', signal: 'Multi-factor', benchmark: 'Russell 2000', nstock: 50, max_pos: 10, bnd_sec: 7, bnd_adv: 3, bnd_turnover: 20, bnd_factor: 50, bnd_rel_weight: 0.5, soft_bnd: true },
  tb_sp500: { method: 'long_simple', signal: 'TrueBeats', benchmark: 'S&P 500', nstock: 40, max_pos: 10, bnd_sec: 7, bnd_adv: 3, bnd_turnover: 20, bnd_factor: 50, bnd_rel_weight: 0.5, soft_bnd: true },
  value_r2000: { method: 'long_simple', signal: 'Value / Quality', benchmark: 'Russell 2000', nstock: 40, max_pos: 10, bnd_sec: 7, bnd_adv: 3, bnd_turnover: 20, bnd_factor: 50, bnd_rel_weight: 0.5, soft_bnd: true },
  momentum_r1000: { method: 'long_simple', signal: 'Momentum', benchmark: 'Russell 1000', nstock: 40, max_pos: 10, bnd_sec: 7, bnd_adv: 3, bnd_turnover: 20, bnd_factor: 50, bnd_rel_weight: 0.5, soft_bnd: true },
  sig_indexEnh_sp500: { method: 'index_enhance', signal: 'Multi-factor', benchmark: 'S&P 500', nstock: 50, max_pos: 3, bnd_sec: 7, bnd_adv: 3, bnd_turnover: 20, bnd_factor: 50, bnd_rel_weight: 0.5, soft_bnd: true },
  sig_longOpt_sp500_50: { method: 'long_optimize', signal: 'Multi-factor', benchmark: 'S&P 500', nstock: 50, max_pos: 3, bnd_sec: 7, bnd_adv: 3, bnd_turnover: 20, bnd_factor: 50, bnd_rel_weight: 0.5, soft_bnd: true },
  sig_longSim_sp500_40: { method: 'long_simple', signal: 'Multi-factor', benchmark: 'S&P 500', nstock: 40, max_pos: 10, bnd_sec: 7, bnd_adv: 3, bnd_turnover: 20, bnd_factor: 50, bnd_rel_weight: 0.5, soft_bnd: true },
  sig_r2000_top_100: { method: 'long_simple', signal: 'Multi-factor', benchmark: 'Russell 2000', nstock: 100, max_pos: 3, bnd_sec: 7, bnd_adv: 3, bnd_turnover: 20, bnd_factor: 50, bnd_rel_weight: 0.5, soft_bnd: true },
  powerlist_r2000_100: { method: 'long_simple', signal: 'Multi-factor', benchmark: 'Russell 2000', nstock: 100, max_pos: 1, bnd_sec: 200, bnd_adv: 10, bnd_turnover: 500, bnd_factor: 500, bnd_rel_weight: 10, soft_bnd: false },
};

const METHOD_LABELS = {
  long_simple: 'Long Simple',
  long_optimize: 'Long Optimize',
  index_enhance: 'Index Enhance',
};

const CONSTRUCTION_TERM_INFO = {
  method: 'How the portfolio is built. Long Simple ranks and selects top stocks; Long Optimize uses constrained optimization; Index Enhance stays close to benchmark weights while tilting to higher-signal names.',
  signal: 'The ranking input used to select and weight stocks — e.g. multi-factor blend, TrueBeats earnings, value/quality, or momentum.',
  benchmark: 'The reference index for performance comparison and for sector/factor constraint targets.',
  nstock: 'Target number of stocks held in the portfolio at each rebalance.',
  rebalance: 'How often holdings are reviewed and traded back toward target weights and signal ranks.',
  soft_bnd: 'When on, allows a slightly broader candidate set during rebalancing to reduce unnecessary turnover from small rank changes.',
  max_pos: 'Maximum weight allowed in any single stock, as a percent of the portfolio.',
  bnd_sec: 'How far sector weights can deviate from the benchmark. For example ±7% means Technology can be at most 7 percentage points overweight or underweight vs the index.',
  bnd_adv: 'Liquidity cap based on trading volume: a position cannot exceed this percent of the stock’s average daily trading volume (scaled to the model’s book size).',
  bnd_turnover: 'Maximum portfolio turnover allowed in a rebalance period, limiting trading costs and churn.',
  bnd_factor: 'Limits how far portfolio factor exposures (size, value, growth, momentum, volatility, etc.) can tilt from the benchmark, measured in standard deviations (σ). ±0.5σ is a modest style tilt.',
  bnd_rel_weight: 'For index-enhancement models, the max deviation of an individual stock’s weight from its benchmark weight.',
};

function getModelConstruction(model) {
  if (!model || model.model_type === 'etf') return null;
  return MODEL_CONSTRUCTION[model.model_id] || null;
}

function getModelMethodology(model) {
  if (!model) return null;
  if (model.model_type === 'etf') return model.model_description || null;
  return MODEL_METHODOLOGY[model.model_id] || model.model_description || null;
}

function DetailTermLabel({ termKey, label }) {
  return (
    <dt className="text-xs uppercase tracking-wide text-text-300 flex items-center gap-1.5">
      <span>{label}</span>
      {CONSTRUCTION_TERM_INFO[termKey] && (
        <div className="group relative inline-flex">
          <InformationCircleIcon className="h-3.5 w-3.5 text-text-300 hover:text-text-100 cursor-help" aria-hidden="true" />
          <div className="absolute bottom-full left-1/2 z-20 mb-2 w-64 -translate-x-1/2 rounded-lg bg-bg-300 px-3 py-2 text-xs font-normal normal-case tracking-normal text-text-100 shadow-lg opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100">
            {CONSTRUCTION_TERM_INFO[termKey]}
            <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-bg-300" />
          </div>
        </div>
      )}
    </dt>
  );
}



const stats = [
  { name: 'Annual Return', key: 'ann_ret' },
  { name: 'Sharpe Ratio', key: 'sharpe' },
  { name: 'Number of Holdings', key: 'no_stocks' },
  { name: 'Excess Return', key: 'excess_ret' },
  { name: 'Information Ratio', key: 'ir' },
  { name: 'Max Drawdown', key: 'maxdrawdown' },
];

// Helper to load PNG as image for jsPDF
const loadLogoImage = () => {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.src = '/wordmark-white.png'; // Use white logo version
    img.onload = () => resolve({
      src: img,
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
      format: 'PNG',
    });
  });
};

const historicalNumberColumns = [
  { key: 'year', label: 'Year', align: 'left' },
  { key: 'no_stocks', label: '# of Holdings', align: 'right' },
  { key: 'ann_ret', label: 'Annual Return (%)', align: 'right' },
  { key: 'bm_return', label: 'Benchmark Return (%)', align: 'right' },
  { key: 'excess_ret', label: 'Excess Return (%)', align: 'right' },
  { key: 'sharpe', label: 'Sharpe Ratio', align: 'right' },
  { key: 'ir', label: 'Information Ratio', align: 'right' },
  { key: 'month_turnover', label: 'Monthly Turnover (%)', align: 'right' },
  { key: 'maxdrawdown', label: 'Max Drawdown (%)', align: 'right' },
  { key: 'maxdrawdown_b', label: 'Bm Max Drawdown (%)', align: 'right' },
];

// Performance Chart Component
const PerformanceChart = ({ modelId, modelType, performanceData }) => {
  const [monthlyData, setMonthlyData] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('All');
  const [periodReturns, setPeriodReturns] = useState({});
  const [loading, setLoading] = useState(true);
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const [startDate, endDate] = customDateRange;
  const [showDatePicker, setShowDatePicker] = useState(false);
  const isETF = modelType === 'etf';
  
  // Benchmark selector state
  const [selectedBenchmark, setSelectedBenchmark] = useState('none');
  const [benchmarkData, setBenchmarkData] = useState([]);
  
  const benchmarkOptions = [
    { value: 'none', label: 'No Benchmark' },
    { value: 'sp500', label: 'S&P 500' },
    { value: 'r1000', label: 'Russell 1000' },
    { value: 'r2000', label: 'Russell 2000' },
    { value: 'r3000', label: 'Russell 3000' }
  ];

  const periods = [
    { label: '1M', value: '1M' },
    { label: '3M', value: '3M' },
    { label: '6M', value: '6M' },
    { label: '1Y', value: '1Y' },
    { label: '5Y', value: '5Y' },
    { label: 'All', value: 'All' }
  ];

  // Function to handle period button clicks
  const handlePeriodClick = (period) => {
    setSelectedPeriod(period);
    if (period !== 'Custom') {
      setCustomDateRange([null, null]);
      setShowDatePicker(false);
    }
  };

  // Function to prefill date range based on period
  const prefillDateRange = (period) => {
    if (!monthlyData || monthlyData.length === 0) return;
    
    const sortedData = [...monthlyData].sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));
    const lastDataDate = parseLocalDate(sortedData[sortedData.length - 1].date);
    const firstDataDate = parseLocalDate(sortedData[0].date);
    
    let startDate;
    switch (period) {
      case '1M':
        startDate = new Date(lastDataDate.getFullYear(), lastDataDate.getMonth() - 1, lastDataDate.getDate());
        break;
      case '3M':
        startDate = new Date(lastDataDate.getFullYear(), lastDataDate.getMonth() - 3, lastDataDate.getDate());
        break;
      case '6M':
        startDate = new Date(lastDataDate.getFullYear(), lastDataDate.getMonth() - 6, lastDataDate.getDate());
        break;
      case '1Y':
        startDate = new Date(lastDataDate.getFullYear() - 1, lastDataDate.getMonth(), lastDataDate.getDate());
        break;
      case '5Y':
        startDate = new Date(lastDataDate.getFullYear() - 5, lastDataDate.getMonth(), lastDataDate.getDate());
        break;
      default:
        startDate = firstDataDate;
    }
    
    setCustomDateRange([startDate, lastDataDate]);
    setSelectedPeriod('Custom');
    setShowDatePicker(true);
  };

  const calculatePeriodReturns = useCallback((data) => {
    if (!data || data.length === 0) return {};
    
    const sortedData = [...data].sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));
    // Use the last data date instead of current date
    const lastDataDate = parseLocalDate(sortedData[sortedData.length - 1].date);
    const returns = {};
    
    // All time - calculate cumulative return over entire period
    returns.All = calculateCumulativeReturn(sortedData);
    console.log('All time data points:', sortedData.length, 'return:', returns.All);
    
    // 5Y - from last data date
    const fiveYearsAgo = new Date(lastDataDate.getFullYear() - 5, lastDataDate.getMonth(), lastDataDate.getDate());
    const fiveYearData = sortedData.filter(d => parseLocalDate(d.date) >= fiveYearsAgo);
    returns['5Y'] = calculateCumulativeReturn(fiveYearData);
    console.log('5Y data points:', fiveYearData.length, 'return:', returns['5Y']);
    
    // 1Y - from last data date
    const oneYearAgo = new Date(lastDataDate.getFullYear() - 1, lastDataDate.getMonth(), lastDataDate.getDate());
    const oneYearData = sortedData.filter(d => parseLocalDate(d.date) >= oneYearAgo);
    returns['1Y'] = calculateCumulativeReturn(oneYearData);
    console.log('1Y data points:', oneYearData.length, 'return:', returns['1Y']);
    
    // 6M - from last data date
    const sixMonthsAgo = new Date(lastDataDate.getFullYear(), lastDataDate.getMonth() - 6, lastDataDate.getDate());
    const sixMonthData = sortedData.filter(d => parseLocalDate(d.date) >= sixMonthsAgo);
    returns['6M'] = calculateCumulativeReturn(sixMonthData);
    console.log('6M data points:', sixMonthData.length, 'return:', returns['6M']);
    
    // 3M - from last data date  
    const threeMonthsAgo = new Date(lastDataDate.getFullYear(), lastDataDate.getMonth() - 3, lastDataDate.getDate());
    const threeMonthData = sortedData.filter(d => parseLocalDate(d.date) >= threeMonthsAgo);
    returns['3M'] = calculateCumulativeReturn(threeMonthData);
    console.log('3M data points:', threeMonthData.length, 'return:', returns['3M']);
    
    // 1M - get the last month of data
    const oneMonthAgo = new Date(lastDataDate.getFullYear(), lastDataDate.getMonth() - 1, lastDataDate.getDate());
    const oneMonthData = sortedData.filter(d => parseLocalDate(d.date) >= oneMonthAgo);
    returns['1M'] = calculateCumulativeReturn(oneMonthData);
    console.log('1M data points:', oneMonthData.length, 'return:', returns['1M']);
    
    return returns;
  }, []);

  // Fetch benchmark data when selected
  useEffect(() => {
    const fetchBenchmarkData = async () => {
      if (selectedBenchmark === 'none') {
        setBenchmarkData([]);
        return;
      }
      
      // Wait for monthlyData to be loaded to get the start date
      if (!monthlyData || monthlyData.length === 0) {
        return;
      }
      
      try {
        const token = localStorage.getItem('access_token');
        
        // Get the earliest date from the portfolio's monthly data
        const sortedData = [...monthlyData].sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));
        const startDate = sortedData[0].date;
        
        console.log('Fetching benchmark data from:', startDate);
        
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/benchmarks`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            start_date: startDate
          }
        });
        
        console.log('Benchmark data received:', response.data.length, 'items');
        setBenchmarkData(response.data);
      } catch (error) {
        console.error('Error fetching benchmark data:', error);
      }
    };

    fetchBenchmarkData();
  }, [selectedBenchmark, monthlyData]);

  useEffect(() => {
    const fetchMonthlyReturns = async () => {
      try {
        const token = localStorage.getItem('access_token');
        // Use different endpoint based on model type
        const endpoint = isETF 
          ? `${process.env.REACT_APP_API_URL}/models/${modelId}/etf-returns`
          : `${process.env.REACT_APP_API_URL}/models/${modelId}/monthly-returns`;
        
        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        const data = response.data;
        console.log('Monthly data received:', data.length, 'items');
        console.log('Date range:', data.length > 0 ? `${data[0].date} to ${data[data.length-1].date}` : 'No data');
        setMonthlyData(data);
        
        // Calculate period returns
        const returns = calculatePeriodReturns(data);
        setPeriodReturns(returns);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching monthly returns:', error);
        setLoading(false);
      }
    };

    fetchMonthlyReturns();
  }, [modelId, isETF, calculatePeriodReturns]);

  // Recalculate period returns when data changes
  useEffect(() => {
    if (monthlyData.length > 0) {
      const returns = calculatePeriodReturns(monthlyData);
      setPeriodReturns(returns);
    }
  }, [monthlyData, performanceData, calculatePeriodReturns]);

  const calculateCumulativeReturn = (data) => {
    if (!data || data.length === 0) return 0;
    
    let cumulative = 1;
    data.forEach((item, index) => {
      // ea_return is already in percentage format from API
      const monthlyReturn = (item.ea_return || 0) / 100;
      cumulative *= (1 + monthlyReturn);
      
      // Debug for first few and last few items
      if (index < 3 || index >= data.length - 3) {
        console.log(`Month ${index + 1} (${item.date}): ${item.ea_return}% -> cumulative: ${((cumulative - 1) * 100).toFixed(2)}%`);
      }
    });
    
    const totalReturn = (cumulative - 1) * 100;
    console.log(`Final cumulative return: ${totalReturn.toFixed(2)}% over ${data.length} months`);
    return totalReturn;
  };

  const getFilteredData = () => {
    if (!monthlyData || monthlyData.length === 0) return [];
    
    const sortedData = [...monthlyData].sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));
    
    // Use the last data date for filtering (monthly rebalances)
    const lastDataDate = parseLocalDate(sortedData[sortedData.length - 1].date);
    let filteredData = [];
    
    // Check if custom date range is set
    if (selectedPeriod === 'Custom' && startDate && endDate) {
      filteredData = sortedData.filter(d => {
        const itemDate = parseLocalDate(d.date);
        return itemDate >= startDate && itemDate <= endDate;
      });
    } else {
      switch (selectedPeriod) {
        case '1M':
          const oneMonthAgo = new Date(lastDataDate.getFullYear(), lastDataDate.getMonth() - 1, lastDataDate.getDate());
          filteredData = sortedData.filter(d => parseLocalDate(d.date) >= oneMonthAgo);
          break;
        case '3M':
          const threeMonthsAgo = new Date(lastDataDate.getFullYear(), lastDataDate.getMonth() - 3, lastDataDate.getDate());
          filteredData = sortedData.filter(d => parseLocalDate(d.date) >= threeMonthsAgo);
          break;
        case '6M':
          const sixMonthsAgo = new Date(lastDataDate.getFullYear(), lastDataDate.getMonth() - 6, lastDataDate.getDate());
          filteredData = sortedData.filter(d => parseLocalDate(d.date) >= sixMonthsAgo);
          break;
        case '1Y':
          const oneYearAgo = new Date(lastDataDate.getFullYear() - 1, lastDataDate.getMonth(), lastDataDate.getDate());
          filteredData = sortedData.filter(d => parseLocalDate(d.date) >= oneYearAgo);
          break;
        case '5Y':
          const fiveYearsAgo = new Date(lastDataDate.getFullYear() - 5, lastDataDate.getMonth(), lastDataDate.getDate());
          filteredData = sortedData.filter(d => parseLocalDate(d.date) >= fiveYearsAgo);
          break;
        default:
          filteredData = sortedData;
      }
    }
    
    console.log(`Filtered data for ${selectedPeriod}:`, filteredData.length, 'items');
    
    // Merge with benchmark data if selected
    if (selectedBenchmark !== 'none' && benchmarkData.length > 0) {
      // The API returns columns directly as sp500, r1000, etc
      const benchmarkColumn = selectedBenchmark;
      
      // Create a map of benchmark data by year-month, keeping the last day of each month
      const benchmarkByMonth = {};
      benchmarkData.forEach(item => {
        const itemDate = parseLocalDate(item.date); // Parse as local date
        const yearMonth = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}`;
        
        // Keep the latest date for each month
        if (!benchmarkByMonth[yearMonth] || item.date > benchmarkByMonth[yearMonth].date) {
          benchmarkByMonth[yearMonth] = item;
        }
      });
      
      console.log('Benchmark months available:', Object.keys(benchmarkByMonth).length);
      console.log('Sample benchmark months:', Object.keys(benchmarkByMonth).slice(0, 5));
      
      filteredData = filteredData.map((item, idx) => {
        const itemDate = parseLocalDate(item.date); // Parse as local date
        const yearMonth = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}`;
        
        const bmData = benchmarkByMonth[yearMonth];
        
        // Debug first few matches
        if (idx < 5) {
          console.log(`Portfolio: ${item.date} (${yearMonth}) → BM: ${bmData?.date} → ${benchmarkColumn}: ${bmData?.[benchmarkColumn]}%`);
        }
        
        return {
          ...item,
          custom_benchmark_return: bmData?.[benchmarkColumn] || null,
          custom_benchmark_cumulative_raw: bmData?.[`${selectedBenchmark}_cumulative`] || null
        };
      });
    }
    
    // Calculate cumulative returns for chart display starting from first point
    let cumulativeEA = 1;
    let cumulativeBM = 1;
    let baselineCustomBM = null; // Will store the first non-null cumulative value
    
    return filteredData.map((item, index) => {
      cumulativeEA *= (1 + (item.ea_return || 0) / 100);
      cumulativeBM *= (1 + (item.benchmark_return || 0) / 100);
      
      // For custom benchmark, rebase the API's cumulative to start from 0
      // Using proper compounding: rebased = (1 + current%) / (1 + baseline%) - 1
      let customBMCumulative = null;
      if (item.custom_benchmark_cumulative_raw !== null && item.custom_benchmark_cumulative_raw !== undefined) {
        if (baselineCustomBM === null) {
          baselineCustomBM = item.custom_benchmark_cumulative_raw;
          customBMCumulative = 0;
        } else {
          customBMCumulative = ((1 + item.custom_benchmark_cumulative_raw / 100) / (1 + baselineCustomBM / 100) - 1) * 100;
        }
      }
      
      return {
        ...item,
        ea_return_cumulative: (cumulativeEA - 1) * 100,
        benchmark_return_cumulative: (cumulativeBM - 1) * 100,
        custom_benchmark_cumulative: customBMCumulative
      };
    });
  };

  const formatReturn = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    const color = value >= 0 ? 'text-green-400' : 'text-red-400';
    const sign = value >= 0 ? '+' : '';
    return <span className={color}>{sign}{value.toFixed(2)}%</span>;
  };

  const chartData = getFilteredData();

  if (loading) {
    return (
      <div className="bg-bg-200 rounded-lg p-6">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-bg-300 h-10 w-10"></div>
          <div className="flex-1 space-y-2 py-1">
            <div className="h-4 bg-bg-300 rounded w-3/4"></div>
            <div className="h-4 bg-bg-300 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-bg-200 rounded-lg shadow-sm border border-bg-300 p-6">
      {/* Performance Summary Table */}
      <div className="mb-6">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-4">
          {periods.map(period => (
            <div key={period.value} className="text-center">
              <div className="text-xs sm:text-sm text-text-300 mb-1">{period.label}</div>
              <div className="text-base sm:text-lg font-semibold">
                {formatReturn(periodReturns[period.value])}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Period Selection Buttons, Benchmark Selector, and Date Picker */}
      <div className="flex flex-col items-center mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full gap-3">
          {/* Period buttons */}
          <div className="flex justify-center">
            <div className="flex flex-wrap bg-bg-300 rounded-lg p-1 gap-0.5">
              {periods.map(period => (
                <button
                  key={period.value}
                  onClick={() => handlePeriodClick(period.value)}
                  className={`px-2.5 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                    selectedPeriod === period.value
                      ? 'bg-primary-100 text-white'
                      : 'text-text-200 hover:text-text-100'
                  }`}
                >
                  {period.label}
                </button>
              ))}
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className={`px-2.5 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors flex items-center ${
                  selectedPeriod === 'Custom'
                    ? 'bg-primary-100 text-white'
                    : 'text-text-200 hover:text-text-100'
                }`}
              >
                <CalendarIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                Custom
              </button>
            </div>
          </div>
          
          {/* Benchmark selector */}
          <div className="flex items-center justify-center sm:justify-end space-x-2">
            <label className="text-xs sm:text-sm text-text-300">Compare to:</label>
            <select
              value={selectedBenchmark}
              onChange={(e) => setSelectedBenchmark(e.target.value)}
              className="bg-bg-300 text-text-100 px-3 py-1 rounded-md text-xs sm:text-sm border border-bg-200 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              {benchmarkOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Date Picker Section */}
        {showDatePicker && (
          <div className="bg-bg-300 rounded-lg p-4 shadow-lg">
            <div className="flex flex-col space-y-4">
              <div className="flex items-center justify-center space-x-4">
                <div className="text-sm text-text-200">Date Range:</div>
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    setCustomDateRange(update);
                    if (update[0] && update[1]) {
                      setSelectedPeriod('Custom');
                    }
                  }}
                  placeholderText="Select date range"
                  className="bg-bg-200 text-text-100 px-3 py-2 rounded-md text-sm border border-bg-300 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  calendarClassName="bg-bg-200 text-text-100 border border-bg-300 rounded-lg shadow-lg"
                  dayClassName={(date) => "text-text-200 hover:bg-primary-100 hover:text-white rounded"}
                />
              </div>
              
              {/* Quick fill buttons */}
              <div className="flex justify-center space-x-2">
                <div className="text-xs text-text-300 mr-2">Quick fill:</div>
                {periods.slice(0, -1).map(period => (
                  <button
                    key={period.value}
                    onClick={() => prefillDateRange(period.value)}
                    className="px-2 py-1 text-xs bg-bg-200 text-text-200 hover:text-text-100 hover:bg-bg-100 rounded transition-colors"
                  >
                    {period.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid 
              stroke="#4a5568" 
              strokeWidth={1}
              strokeDasharray="3 3"
              opacity={0.6}
            />
            <XAxis 
              dataKey="date" 
              tick={{ fill: 'white', fontSize: 12 }}
              tickFormatter={(value) => {
                const date = parseLocalDate(value);
                if (!date) return '';
                // For very short periods, show month/day, otherwise just show year
                if (selectedPeriod === '1M') {
                  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                }
                return String(date.getFullYear());
              }}
              tickLine={false}
              axisLine={false}
              interval={0}
              padding={{ left: 16, right: 16 }}
              tickMargin={8}
              ticks={(() => {
                if (!chartData || chartData.length === 0) return [];
                
                if (selectedPeriod === '1M') {
                  // For 1M, show all ticks
                  return chartData.map(item => item.date);
                } else {
                  // For other periods, show only one tick per year
                  const yearTicks = [];
                  const seenYears = new Set();
                  
                  chartData.forEach(item => {
                    const date = parseLocalDate(item.date);
                    if (!date) return;
                    const year = date.getFullYear();
                    if (!seenYears.has(year)) {
                      seenYears.add(year);
                      yearTicks.push(item.date);
                    }
                  });
                  
                  return yearTicks;
                }
              })()}
            />
            <YAxis 
              width={78}
              tick={{ fill: 'white', fontSize: 12 }}
              tickFormatter={(value) => `${value.toFixed(1)}%`}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#292a37', border: 'none' }}
              labelStyle={{ color: '#fff' }}
              itemStyle={{ color: '#fff' }}
              formatter={(value, name) => [`${value.toFixed(2)}%`, name]}
              labelFormatter={(label) => {
                // Parse as local date to avoid timezone shifts
                const [year, month] = label.split('-');
                const date = new Date(year, month - 1, 1);
                return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
              }}
            />
            <Line 
              type="monotone" 
              dataKey="ea_return_cumulative" 
              stroke="#2E5BFF" 
              strokeWidth={2}
              dot={false}
              name="Portfolio Return"
            />
            {/* For ETF models, hide the default benchmark line when no benchmark is selected */}
            {!(isETF && selectedBenchmark === 'none') && (
              <Line 
                type="monotone" 
                dataKey="benchmark_return_cumulative" 
                stroke="#00D4FF" 
                strokeWidth={2}
                dot={false}
                name="Benchmark Return"
              />
            )}
            {selectedBenchmark !== 'none' && (
              <Line 
                type="monotone" 
                dataKey="custom_benchmark_cumulative" 
                stroke="#FFA500" 
                strokeWidth={2}
                dot={false}
                name={benchmarkOptions.find(b => b.value === selectedBenchmark)?.label || 'Custom Benchmark'}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Wrapper: resolves the model from router state (in-app navigation) or from the
// URL param + API (direct load / refresh / shared link), then renders the page.
export default function ModelPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { model: stateModel, activeTab } = location.state || {};
  const stateModelMatches = stateModel && String(stateModel.id) === String(id);

  const [fetchedModel, setFetchedModel] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (stateModelMatches) return undefined;
    let cancelled = false;
    setFetchedModel(null);
    setNotFound(false);

    const loadModel = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/models`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const found = response.data.find(m => String(m.id) === String(id));
        if (found) setFetchedModel(found);
        else setNotFound(true);
      } catch (error) {
        console.error('Error fetching model:', error);
        if (!cancelled) setNotFound(true);
      }
    };

    loadModel();
    return () => { cancelled = true; };
  }, [id, stateModelMatches]);

  const resolvedModel = stateModelMatches ? stateModel : fetchedModel;

  if (!resolvedModel) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-100 text-text-200">
        {notFound ? (
          <div className="text-center">
            <p className="mb-4">We couldn't load this model.</p>
            <button
              onClick={() => navigate('/models')}
              className="px-4 py-2 bg-bg-200 text-text-100 rounded hover:bg-bg-300"
            >
              Back to Models
            </button>
          </div>
        ) : (
          <p>Loading model…</p>
        )}
      </div>
    );
  }

  return <ModelPageContent key={resolvedModel.id} initialModel={resolvedModel} activeTab={activeTab} />;
}

function ModelPageContent({ initialModel, activeTab }) {
  const { userRecord } = useAuth();
  const [model, setModel] = useState(initialModel);
  const [performanceData, setPerformanceData] = useState([]);
  const [sectorData, setSectorData] = useState([]);
  const [factorData, setFactorData] = useState([]);

  const [currentTab, setCurrentTab] = useState(activeTab || 'Performance');

  const [holdingsData, setHoldingsData] = useState(null);
  const [holdingsLoading, setHoldingsLoading] = useState(true);
  const [selectedHoldingsDate, setSelectedHoldingsDate] = useState(null);
  const [availableHoldingsDates, setAvailableHoldingsDates] = useState([]);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [showScores, setShowScores] = useState(false);
  const [activeScoreTooltip, setActiveScoreTooltip] = useState(null);

  const chartRef = useRef(null);
  const chartJsRef = useRef(null);

  const tabs = [
    {
      name: 'Performance',
      current: currentTab === 'Performance'
    },
    {
      name: 'Holdings',
      current: currentTab === 'Holdings',
      locked: holdingsData && !holdingsData.user_approved // Only show lock if we have data and user is not approved
    },
    {
      name: 'Methodology',
      current: currentTab === 'Methodology'
    }
  ];

  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate('/models');
  };

  const handleSecurityClick = (security) => {
    navigate(`/ticker/${security.security_id}`, { 
      state: { 
        security,
        returnTo: {
          path: `/model/${model.id}`,
          tab: currentTab
        }
      }
    });
  };

  const handleETFClick = (ticker) => {
    navigate(`/etf/${ticker}`, { 
      state: { 
        ticker,
        returnTo: {
          path: `/model/${model.id}`,
          tab: currentTab
        }
      }
    });
  };

  const handlePreviousHoldingsDate = () => {
    if (holdingsData?.previous_rebalance_date) {
      setSelectedHoldingsDate(holdingsData.previous_rebalance_date);
    }
  };

  const handleNextHoldingsDate = () => {
    if (holdingsData?.next_rebalance_date) {
      setSelectedHoldingsDate(holdingsData.next_rebalance_date);
    }
  };

  const handleLatestHoldingsDate = () => {
    if (holdingsData?.latest_rebalance_date) {
      setSelectedHoldingsDate(holdingsData.latest_rebalance_date);
    }
  };

  const formatHoldingScore = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-text-300">–</span>;
    }
    return Math.round(parseFloat(value)).toString();
  };

  const renderSignalBadge = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-text-300">–</span>;
    }
    const score = parseFloat(value);
    const colorClass =
      score >= 80 ? 'bg-green-400/10 text-green-400' :
      score >= 60 ? 'bg-green-300/10 text-green-300' :
      score >= 40 ? 'bg-yellow-300/10 text-yellow-300' :
      score >= 20 ? 'bg-red-300/10 text-red-300' :
      'bg-red-400/10 text-red-400';
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${colorClass}`}>
        {Math.round(score)}
      </span>
    );
  };

  const SCORE_COLUMNS = [
    { key: 'value', label: 'Value' },
    { key: 'momentum', label: 'Momentum' },
    { key: 'quality', label: 'Quality' },
    { key: 'lowvol', label: 'Low Vol' },
    { key: 'earnings', label: 'Earnings' },
    { key: 'transcripts', label: 'Transcripts' },
    { key: 'innovation', label: 'Innovation' },
  ];

  const ETF_SCORE_COLUMNS = [
    { key: 'value_score', label: 'Value' },
    { key: 'momentum_score', label: 'Momentum' },
    { key: 'quality_score', label: 'Quality' },
    { key: 'lowvol_score', label: 'Low Vol' },
    { key: 'blend_score', label: 'Blend' },
    { key: 'blend2_score', label: 'Blend2' },
  ];

  const renderScoreHeader = (factorKey, label) => (
    <th
      key={factorKey}
      className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 relative"
    >
      <div className="flex items-center justify-end">
        {label}
        {signalDescriptions[factorKey] && (
          <div className="relative">
            <button
              type="button"
              className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                setActiveScoreTooltip((prev) => (prev === factorKey ? null : factorKey));
              }}
              onMouseEnter={() => setActiveScoreTooltip(factorKey)}
              onMouseLeave={() => setActiveScoreTooltip(null)}
              aria-label={`Information about ${label}`}
            >
              <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
            </button>
            {activeScoreTooltip === factorKey && (
              <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                {signalDescriptions[factorKey]}
              </div>
            )}
          </div>
        )}
      </div>
    </th>
  );

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'desc' };
    });
  };

  const sortedPerformanceData = React.useMemo(() => {
    if (!sortConfig.key) return performanceData;
    const sorted = [...performanceData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [performanceData, sortConfig]);

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  // Fetch fresh model data on mount to get latest metrics
  useEffect(() => {
    const fetchModelData = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/models`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        // Find this specific model in the response
        const freshModel = response.data.find(m => m.id === model.id);
        if (freshModel) {
          setModel(freshModel);
        }
      } catch (error) {
        console.error('Error fetching fresh model data:', error);
      }
    };

    if (model?.id) {
      fetchModelData();
    }
  }, [model?.id]);

  useEffect(() => {
    const fetchModelReturns = async () => {
      try {
        const token = localStorage.getItem('access_token');
        // Use different endpoint based on model type
        const endpoint = model.model_type === 'etf'
          ? `${process.env.REACT_APP_API_URL}/models/${model.id}/etf-annual-returns`
          : `${process.env.REACT_APP_API_URL}/models/${model.id}/returns`;
        
        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setPerformanceData(response.data);
      } catch (error) {
        console.error('Error fetching model returns:', error);
      }
    };

    fetchModelReturns();
  }, [model.id, model.model_type]);

  useEffect(() => {
    const fetchModelSector = async () => {
      try {
        const token = localStorage.getItem('access_token'); // Retrieve the token from local storage
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/models/${model.id}/sector`, {
          headers: {
            Authorization: `Bearer ${token}`, // Set the Authorization header
          },
        });
        setSectorData(response.data);
      } catch (error) {
        console.error('Error fetching model sector data:', error);
      }
    };

    fetchModelSector();
  }, [model.id]);

  useEffect(() => {
    const fetchModelFactor = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/models/${model.id}/factors`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setFactorData(response.data);
      } catch (error) {
        console.error('Error fetching model sector data:', error);
      }
    };

    fetchModelFactor();
  }, [model.id]);

  useEffect(() => {
    if (currentTab === 'Holdings') {
      const script = document.createElement('script');
      script.src = "https://js.hsforms.net/forms/embed/22063250.js";
      script.defer = true;
      document.body.appendChild(script);

      // Cleanup function to remove the script when the component unmounts or tab changes
      return () => {
        if (script.parentNode) {
          document.body.removeChild(script);
        }
      };
    }
  }, [currentTab]); // Run effect when currentTab changes

  useEffect(() => {
    setSelectedHoldingsDate(null);
    setAvailableHoldingsDates([]);
    setHoldingsData(null);
  }, [model.id]);

  useEffect(() => {
    const fetchHoldings = async () => {
      if (currentTab === 'Holdings') {
        setHoldingsLoading(true);
        try {
          const token = localStorage.getItem('access_token');
          // Use different endpoint based on model type
          let endpoint = model.model_type === 'etf'
            ? `${process.env.REACT_APP_API_URL}/models/${model.id}/etf-holdings`
            : `${process.env.REACT_APP_API_URL}/models/${model.id}/holdings`;

          if (model.model_type !== 'etf' && selectedHoldingsDate) {
            endpoint += `?as_of_date=${encodeURIComponent(selectedHoldingsDate)}`;
          }
          
          const response = await axios.get(endpoint, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          setHoldingsData(response.data);
          if (model.model_type !== 'etf') {
            setAvailableHoldingsDates(response.data.available_dates || []);
            if (!selectedHoldingsDate && response.data.portfolio_date) {
              setSelectedHoldingsDate(response.data.portfolio_date);
            }
          }
        } catch (error) {
          console.error('Error fetching holdings data:', error);
        } finally {
          setHoldingsLoading(false);
        }
      }
    };

    fetchHoldings();
  }, [model.id, model.model_type, currentTab, selectedHoldingsDate]);

  const csvEscape = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    return /[",\n]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
  };

  const downloadTradesCSV = () => {
    const includeScores = showScores && model.model_type !== 'etf';
    const scoreHeader = includeScores
      ? ',Signal,Value,Momentum,Quality,Low Vol,Earnings,Transcripts,Innovation'
      : '';
    const headers = model.model_type === 'etf'
      ? ['Ticker,Action,Current Weight,Previous Weight,Weight Change\n']
      : [`Ticker,CUSIP,Security Name,Status,Note,Action,Current Weight,Previous Weight,Weight Change${scoreHeader}\n`];
    
    const rows = holdingsData.trades.map(trade => {
      if (model.model_type === 'etf') {
        return `${csvEscape(trade.ticker)},${csvEscape(trade.action)},${trade.current_weight},${trade.previous_weight},${trade.percentage_change}\n`;
      } else {
        const base = [
          trade.ticker,
          trade.cusip,
          trade.name,
          trade.security_status,
          trade.inactive_note,
          trade.action,
          trade.current_weight,
          trade.previous_weight,
          trade.percentage_change
        ];
        if (includeScores) {
          base.push(
            trade.signal_weight ?? '',
            trade.component_scores?.value ?? '',
            trade.component_scores?.momentum ?? '',
            trade.component_scores?.quality ?? '',
            trade.component_scores?.lowvol ?? '',
            trade.component_scores?.earnings ?? '',
            trade.component_scores?.transcripts ?? '',
            trade.component_scores?.innovation ?? ''
          );
        }
        return base.map(csvEscape).join(',') + '\n';
      }
    });
    
    const csvContent = headers.concat(rows).join('');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `trades_${holdingsData.portfolio_date}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to download current holdings as CSV
  const downloadCurrentHoldingsCSV = () => {
    const holdings = holdingsData.current_holdings || holdingsData.holdings;
    
    // Create CSV content based on model type
    if (model.model_type === 'etf') {
      const includeScores = showScores;
      const headers = includeScores
        ? ['Ticker,Weight (%),Value,Momentum,Quality,Low Vol,Blend,Blend2\n']
        : ['Ticker,Weight (%)\n'];
      const rows = holdings.map(holding => {
        if (!includeScores) {
          return `${holding.ticker},${holding.weight.toFixed(2)}\n`;
        }
        return [
          holding.ticker,
          holding.weight.toFixed(2),
          holding.value_score ?? '',
          holding.momentum_score ?? '',
          holding.quality_score ?? '',
          holding.lowvol_score ?? '',
          holding.blend_score ?? '',
          holding.blend2_score ?? ''
        ].map(csvEscape).join(',') + '\n';
      });
      const csvContent = headers.concat(rows).join('');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `etf_holdings_${holdingsData.portfolio_date}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Stock model CSV
      const includeScores = showScores;
      const scoreHeader = includeScores
        ? ',Signal,Value,Momentum,Quality,Low Vol,Earnings,Transcripts,Innovation'
        : '';
      const headers = [`Ticker,CUSIP,Security Name,Status,Note,Price,Weight (%)${scoreHeader}\n`];
      const rows = holdings.map(holding => {
        const base = [
          holding.ticker,
          holding.cusip,
          holding.name,
          holding.security_status,
          holding.inactive_note,
          holding.price || '',
          holding.weight.toFixed(2)
        ];
        if (includeScores) {
          base.push(
            holding.signal_weight ?? '',
            holding.component_scores?.value ?? '',
            holding.component_scores?.momentum ?? '',
            holding.component_scores?.quality ?? '',
            holding.component_scores?.lowvol ?? '',
            holding.component_scores?.earnings ?? '',
            holding.component_scores?.transcripts ?? '',
            holding.component_scores?.innovation ?? ''
          );
        }
        return base.map(csvEscape).join(',') + '\n';
      });
      const csvContent = headers.concat(rows).join('');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `current_holdings_${holdingsData.portfolio_date}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Helper to render Chart.js chart as PNG for PDF
  const renderChartJsAsImage = () => {
    return new Promise((resolve) => {
      const canvas = chartJsRef.current;
      if (!canvas) return resolve(null);
      // Clean up any previous chart instance
      if (canvas._chartInstance) {
        canvas._chartInstance.destroy();
      }

      // Reduce canvas dimensions for better performance
      canvas.width = 900;  // Reduced from 2400
      canvas.height = 450;  // Reduced from 1200

      const years = performanceData.map(d => d.year);
      const portfolio = performanceData.map(d => d.portfolio_10k_growth);
      const benchmark = performanceData.map(d => d.benchmark_10k_growth);
      const chart = new Chart(canvas, {
        type: 'line',
        data: {
          labels: years,
          datasets: [
            {
              label: 'Portfolio Growth',
              data: portfolio,
              borderColor: '#2E5BFF',
              backgroundColor: 'rgba(46,91,255,0.08)',
              borderWidth: 2,  // Reduced from 5
              fill: true,
              pointRadius: 0,
              tension: 0.3,
            },
            {
              label: 'Benchmark Growth',
              data: benchmark,
              borderColor: '#00D4FF',
              backgroundColor: 'rgba(0,212,255,0.08)',
              borderWidth: 2,  // Reduced from 5
              fill: true,
              pointRadius: 0,
              tension: 0.3,
            },
          ],
        },
        options: {
          responsive: false,
          animation: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                font: { size: 10, family: 'Inter, Arial, sans-serif', weight: 'bold' },  // Reduced from 40
                color: '#222',
                boxWidth: 30,  // Reduced from 60
                boxHeight: 15,  // Reduced from 30
              },
            },
            title: { display: false },
            tooltip: { enabled: false },
          },
          layout: { padding: 30 },  // Reduced from 60
          scales: {
            x: {
              title: {
                display: true,
                text: 'Year',
                font: { size: 12, weight: 'bold', family: 'Inter, Arial, sans-serif' },  // Reduced from 48
                color: '#222',
              },
              ticks: {
                font: { size: 10, family: 'Inter, Arial, sans-serif', weight: 'bold' },  // Reduced from 36
                color: '#222',
                maxRotation: 0,
                minRotation: 0,
                autoSkip: false,
              },
              grid: { color: '#eee' },
            },
            y: {
              title: {
                display: true,
                text: 'Value ($)',
                font: { size: 12, weight: 'bold', family: 'Inter, Arial, sans-serif' },  // Reduced from 48
                color: '#222',
              },
              ticks: {
                font: { size: 10, family: 'Inter, Arial, sans-serif', weight: 'bold' },  // Reduced from 36
                color: '#222',
                callback: function(value) { return `$${(value/1000).toFixed(0)}K`; },
              },
              grid: { color: '#eee' },
            },
          },
        },
      });
      canvas._chartInstance = chart;
      setTimeout(() => {
        resolve(canvas.toDataURL('image/png'));
      }, 300); // Wait for chart to render
    });
  };

  const generateCustomPDF = async ({ whitelabel = false, extraText = '' } = {}) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let y = 0;
    const whitelabelSettings = whitelabel ? getWhitelabelSettings(userRecord) : null;
    const primaryColor = whitelabelSettings?.primary_color || '#2E5BFF';
    const primaryRgb = hexToRgb(primaryColor);
    const brandName = whitelabelSettings?.firm_name || 'SharpeMetrix';
    const logoImg = whitelabelSettings?.logo_url
      ? await loadImageFromUrl(whitelabelSettings.logo_url)
      : await loadLogoImage();

    // HEADER (blue bar with white logo and model name)
    // Clean model name (remove "SharpeMetrix" prefix) and place on right, vertically centered
    let cleanModelName = model.model_name;
    if (cleanModelName.toLowerCase().startsWith('sharpemetrix ')) {
      cleanModelName = cleanModelName.substring(13); // Remove "SharpeMetrix " (13 characters)
    }

    await drawPdfHeader(pdf, {
      pageWidth,
      title: cleanModelName,
      logoImg,
      logoText: whitelabelSettings?.firm_name || 'SharpeMetrix',
      primaryColor,
    });
    
    y = 40;

    // PORTFOLIO SUMMARY SECTION
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...primaryRgb);
    pdf.text('Portfolio Summary', 15, y);
    y += 8;
    
    autoTable(pdf, {
      startY: y,
      head: [[
        'Annual Return', 'Sharpe Ratio', 'Number of Holdings', 'Excess Return', 'Information Ratio', 'Max Drawdown'
      ]],
      body: [[
        model.ann_ret != null ? model.ann_ret.toFixed(1) + '%' : 'N/A',
        model.sharpe != null ? model.sharpe.toFixed(2) : 'N/A',
        model.no_stocks != null ? model.no_stocks : 'N/A',
        model.excess_ret != null ? model.excess_ret.toFixed(1) + '%' : 'N/A',
        model.ir != null ? model.ir.toFixed(2) : 'N/A',
        model.maxdrawdown != null ? model.maxdrawdown.toFixed(1) + '%' : 'N/A',
      ]],
      theme: 'striped',
      styles: { 
        fontSize: 11, 
        cellPadding: 4,
        font: 'helvetica',
        textColor: [40, 40, 40],
        lineColor: [220, 220, 220],
        lineWidth: 0.5
      },
      headStyles: { 
        fillColor: primaryRgb, 
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 11
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: { left: 15, right: 15 },
    });
    y = pdf.lastAutoTable.finalY + 15;

    // GROWTH OF $10,000 CHART
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...primaryRgb);
    pdf.text('Growth of $10,000', 15, y);
    y += 8;
    
    const chartImg = await renderChartJsAsImage();
    if (chartImg) {
      pdf.addImage(chartImg, 'PNG', 15, y, 180, 85);
      y += 95;
    }

    // Check if we need a new page for the annual returns table
    if (y > pageHeight - 80) {
      pdf.addPage();
      y = 25;
    }

    // ANNUAL RETURNS TABLE
    if (performanceData && performanceData.length > 0) {
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...primaryRgb);
      pdf.text('Annual Returns', 15, y);
      y += 8;
      
      autoTable(pdf, {
        startY: y,
        head: [[
          'Year', '# Stocks', 'Annual Return (%)', 'Benchmark (%)', 'Excess Return (%)', 'Sharpe', 'Info Ratio', 'Max DD (%)'
        ]],
        body: performanceData.map(row => [
          row.year,
          row.no_stocks ?? '',
          row.ann_ret != null ? row.ann_ret.toFixed(1) : '',
          row.bm_return != null ? row.bm_return.toFixed(1) : '',
          row.excess_ret != null ? row.excess_ret.toFixed(1) : '',
          row.sharpe != null ? row.sharpe.toFixed(2) : '',
          row.ir != null ? row.ir.toFixed(2) : '',
          row.maxdrawdown != null ? row.maxdrawdown.toFixed(1) : '',
        ]),
        theme: 'striped',
        styles: { 
          fontSize: 9, 
          cellPadding: 3,
          font: 'helvetica',
          textColor: [40, 40, 40],
          lineColor: [220, 220, 220],
          lineWidth: 0.5
        },
        headStyles: { 
          fillColor: primaryRgb, 
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250]
        },
        margin: { left: 15, right: 15 },
        pageBreak: 'auto',
      });
      y = pdf.lastAutoTable.finalY + 15;
    }

    // METHODOLOGY PAGE
    pdf.addPage();
    await drawPdfHeader(pdf, {
      pageWidth,
      title: 'Model Portfolio Methodology',
      logoImg,
      logoText: whitelabelSettings?.firm_name || 'SharpeMetrix',
      primaryColor,
    });
    
    y = 45;
    
    // Methodology content
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(40, 40, 40);
    
    // Split methodology text by sections and format headings as bold
    const methodologyLines = methodologyText.split('\n');
    let currentY = y;
    
    methodologyLines.forEach(line => {
      if (line.trim() === '') {
        // Add extra space for blank lines
        currentY += 8;
      } else if (line.includes('Alternative Data Signals') || line.includes('Enhanced Quantitative Factors')) {
        // Add extra space before "Enhanced Quantitative Factors" section
        if (line.includes('Enhanced Quantitative Factors')) {
          currentY += 6;
        }
        // Bold headings
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        const wrappedHeading = pdf.splitTextToSize(line, pageWidth - 30);
        pdf.text(wrappedHeading, 15, currentY);
        currentY += wrappedHeading.length * 5 + 2; // Extra spacing after headings
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
      } else {
        // Regular text
        const wrappedLine = pdf.splitTextToSize(line, pageWidth - 30);
        pdf.text(wrappedLine, 15, currentY);
        currentY += wrappedLine.length * 5;
      }
    });
    
    // Footer with disclaimer
    const disclaimerY = pageHeight - 35;
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    const pdfDisclaimerText = whitelabelSettings
      ? `This report is generated by ${brandName}. Past performance does not predict future results. Consult with a qualified financial advisor before making any investment decisions.`
      : disclaimerText;
    const splitDisclaimer = pdf.splitTextToSize(pdfDisclaimerText, pageWidth - 30);
    pdf.text(splitDisclaimer, 15, disclaimerY);

    addPdfFinalText(pdf, {
      pageWidth,
      pageHeight,
      text: whitelabelSettings ? (extraText || whitelabelSettings.disclosure_text) : '',
      primaryColor,
    });

    addPdfPageNumbers(pdf, pageWidth, pageHeight);

    pdf.save(`${whitelabel ? 'Whitelabel' : 'SharpeMetrix'}_${whitelabel ? cleanModelName : model.model_name}_report.pdf`);
  };

  const canDownloadHoldingsCSV = model?.model_type === 'etf' || holdingsData?.is_latest_date !== false;
  const methodologySummary = model ? getModelMethodology(model) : null;
  const construction = model ? getModelConstruction(model) : null;

  if (!model) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-bg-100 overflow-x-hidden">
      <div className="mt-5 px-4 sm:px-6 lg:px-8">
        <div className="mb-4">
          <button
            type="button"
            onClick={handleBackClick}
            className="inline-flex items-center rounded-md bg-bg-200 px-3 py-2 text-sm font-medium text-text-200 hover:bg-bg-300 hover:text-text-100"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" aria-hidden="true" />
            Back to Models
          </button>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6 mb-4">
          <div className="w-full">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold leading-6 text-text-100">{model.model_name}</h1>
              {model.universe && (
                <span className="inline-flex items-center rounded-full bg-primary-100/10 px-3 py-1 text-xs font-medium text-primary-100 border border-primary-100/20">
                  Benchmark: {model.universe}
                </span>
              )}
            </div>
            <button
              onClick={() => setExportModalOpen(true)}
              className="mt-3 inline-flex items-center rounded-md bg-bg-200 px-3 py-2 text-sm font-medium text-text-200 hover:bg-bg-300 hover:text-text-100"
            >
              Export PDF
            </button>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="mt-6 rounded-2xl border border-bg-300 bg-bg-200/70 p-2 shadow-sm">
          <nav aria-label="Model sections">
            <ul className="grid grid-cols-3 gap-2 text-sm font-semibold">
              {tabs.map((tab) => (
                <li key={tab.name}>
                  <button
                    type="button"
                    className={`flex w-full items-center justify-center rounded-xl px-4 py-3 transition-all ${
                      tab.current
                        ? 'bg-primary-100 text-white shadow-md'
                        : 'bg-bg-100 text-text-200 hover:bg-bg-300 hover:text-text-100'
                    }`}
                    onClick={() => setCurrentTab(tab.name)}
                  >
                    {tab.name}
                    {tab.locked && (
                      <span className="ml-2 text-text-200 text-xs" title="Coming Soon">
                        <LockClosedIcon className="h-4 w-4 inline" aria-hidden="true" />
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        {/* End of Tabs Section */}

        {/* Content based on the selected tab */}
        <div className="mt-6">
          {currentTab === 'Performance' ? (
            // Existing content for Performance tab
            <div>
              <div className="mt-6">
                <h3 className="text-xl font-semibold leading-6 text-text-100 mb-4">Portfolio Summary</h3>
                <dl className="grid grid-cols-1 divide-y divide-bg-300 overflow-hidden rounded-lg bg-bg-200 shadow md:grid-cols-6 md:divide-x md:divide-y-0">
                  {stats.map((stat) => (
                    <div key={stat.name} className="px-2 py-4 sm:p-4">
                      <dt className="text-sm font-medium text-text-100">{stat.name}</dt>
                      <dd className="mt-1 flex items-baseline justify-between md:block lg:flex">
                        <div className="flex items-baseline text-lg font-semibold text-text-100">
                          {model[stat.key] !== undefined && model[stat.key] !== null ? 
                            ['ann_ret', 'excess_ret', 'maxdrawdown', 'month_turnover'].includes(stat.key) 
                              ? `${model[stat.key].toFixed(1)}%` 
                              : stat.key === 'no_stocks' ? model[stat.key] : model[stat.key].toFixed(2)
                            : 'N/A'
                          }
                        </div>
                      </dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-primary-100/20 bg-primary-100/10 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-text-100">Want to see the portfolio positions?</p>
                    <p className="mt-1 text-sm text-text-200">View current holdings, historical holdings, and recent buy/sell changes for this model.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentTab('Holdings')}
                    className="inline-flex items-center justify-center rounded-lg bg-primary-100 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-200"
                  >
                    Go to Holdings
                  </button>
                </div>
              </div>

              {/* Performance Chart Section */}
              <div className="mt-8">
                <h3 className="text-xl font-semibold leading-6 text-text-100 mb-4">Performance Overview</h3>
                <PerformanceChart modelId={model.id} modelType={model.model_type} performanceData={performanceData} />
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold leading-6 text-text-100 mb-4">Growth of $10,000</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart 
                    data={performanceData} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                  >
                    <defs>
                      <linearGradient id="portfolioGrowth" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(46, 91, 255, 0.8)" />
                        <stop offset="100%" stopColor="rgba(46, 91, 255, 0)" />
                      </linearGradient>
                      <linearGradient id="benchmarkGrowth" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(0, 212, 255, 0.8)" />
                        <stop offset="100%" stopColor="rgba(0, 212, 255, 0)" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#2a2b3a" />
                    <XAxis 
                      dataKey="year" 
                      tick={{ fill: 'white' }}
                      tickLine={false}
                      axisLine={false}
                      padding={{ left: 16, right: 16 }}
                      tickMargin={8}
                      label={{ value: 'Year', position: 'insideBottom', offset: -10, style: { fill: 'white' } }}
                    />
                    <YAxis 
                      tick={{ fill: 'white' }} 
                      tickFormatter={(value) => `$${(value/1000).toFixed(0)}K`}
                      label={{ value: 'Value ($)', angle: -90, position: 'insideLeft', offset: -5, style: { fill: 'white' } }}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#292a37', border: 'none' }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value, name, props) => [
                        `$${value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`,
                        name === "Portfolio Growth" ? "Portfolio" : "Benchmark",
                        `Return: ${(props.payload.ann_ret || props.payload.bm_return)}%`
                      ]}
                    />
                    <Legend 
                      wrapperStyle={{ color: 'white', marginBottom: '-15px' }}
                    />
                    <Area 
                      dataKey="portfolio_10k_growth" 
                      stroke="#2E5BFF" 
                      fill="url(#portfolioGrowth)" 
                      name="Portfolio Growth"
                      isAnimationActive={false}
                    />
                    <Area 
                      dataKey="benchmark_10k_growth" 
                      stroke="#00D4FF" 
                      fill="url(#benchmarkGrowth)" 
                      name="Benchmark Growth"
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold leading-6 text-text-100 mb-4">Annual Returns</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={performanceData} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
                    <CartesianGrid stroke="#2a2b3a" />
                    <XAxis 
                      dataKey="year" 
                      tick={{ fill: 'white' }}
                      tickLine={false}
                      axisLine={false}
                      padding={{ left: 16, right: 16 }}
                      tickMargin={8}
                      label={{ value: 'Year', position: 'insideBottom', offset: -10, style: { fill: 'white' } }}
                    />
                    <YAxis 
                      tick={{ fill: 'white' }} 
                      tickFormatter={(tick) => `${tick}%`} 
                      label={{ value: 'Return (%)', angle: -90, position: 'insideLeft', offset: -5, style: { fill: 'white' } }}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#292a37', border: 'none' }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value, name) => [`${value}%`, name]}
                    />
                    <Legend 
                      wrapperStyle={{ color: 'white', marginBottom: '-15px' }}
                      verticalAlign="bottom"
                      align="center"
                    />
                    <Bar dataKey="ann_ret" fill="#2E5BFF" name="Portfolio Return" />
                    <Bar dataKey="bm_return" fill="#00D4FF" name="Benchmark Return" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <br />
              <div className="overflow-x-auto">
                <h3 className="text-xl font-semibold leading-6 text-text-100 mb-4">Historical Performance</h3>
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg bg-bg-200">
                  <div className="m-2" >
                    <table className="min-w-full divide-y divide-bg-300">
                      <thead className="bg-bg-200 sticky top-0 z-10">
                        <tr>
                          {historicalNumberColumns
                            .filter(col => {
                              // Hide excess_ret and month_turnover for ETF models
                              if (model.model_type === 'etf' && (col.key === 'excess_ret' || col.key === 'month_turnover')) {
                                return false;
                              }
                              return true;
                            })
                            .map(col => (
                            <th
                              key={col.key}
                              className={`whitespace-nowrap px-2 py-3.5 text-${col.align} text-sm font-semibold text-text-100 cursor-pointer select-none`}
                              onClick={() => handleSort(col.key)}
                            >
                              <span className="inline-flex items-center">
                                {col.label}
                                <ArrowsUpDownIcon className={`h-4 w-4 ml-1 ${sortConfig.key === col.key ? 'text-primary-100' : 'text-text-300'}`} />
                                <span className="ml-1">{getSortIndicator(col.key)}</span>
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-bg-300 bg-bg-200">
                        {sortedPerformanceData.map((row) => (
                          <tr key={row.id} className="cursor-pointer">
                            <td className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-left">
                              {row.year === new Date().getFullYear() ? `${row.year} (YTD)` : row.year}
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-right">
                              {row.no_stocks !== null && row.no_stocks !== undefined ? row.no_stocks : 'N/A'}
                            </td>
                            <td className="whitespace-normal px-2 py-2 text-sm text-text-200 text-right">
                              {row.ann_ret !== null && row.ann_ret !== undefined ? `${row.ann_ret.toFixed(1)}%` : 'N/A'}
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-right">
                              {row.bm_return !== null && row.bm_return !== undefined ? `${row.bm_return.toFixed(1)}%` : 'N/A'}
                            </td>
                            {model.model_type !== 'etf' && (
                              <td className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-right">
                                {row.excess_ret !== null && row.excess_ret !== undefined ? `${row.excess_ret.toFixed(1)}%` : 'N/A'}
                              </td>
                            )}
                            <td className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-right">
                              {row.sharpe !== null && row.sharpe !== undefined ? row.sharpe.toFixed(2) : 'N/A'}
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-right">
                              {row.ir !== null && row.ir !== undefined ? row.ir.toFixed(2) : 'N/A'}
                            </td>
                            {model.model_type !== 'etf' && (
                              <td className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-right">
                                {row.month_turnover !== null && row.month_turnover !== undefined ? `${row.month_turnover.toFixed(0)}%` : 'N/A'}
                              </td>
                            )}
                            <td className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-right">
                              {row.maxdrawdown !== null && row.maxdrawdown !== undefined ? `${row.maxdrawdown.toFixed(1)}%` : 'N/A'}
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-right">
                              {row.maxdrawdown_b !== null && row.maxdrawdown_b !== undefined ? `${row.maxdrawdown_b.toFixed(1)}%` : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Only show sector and factor exposures for stock models */}
              {model.model_type !== 'etf' && (
              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-1 lg:grid-cols-2">
                <div className="bg-bg-200 shadow-lg rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <h2 className="text-xl font-semibold text-text-100">Sector Exposures</h2>
                    <div className="group relative">
                      <InformationCircleIcon className="h-5 w-5 text-text-300 hover:text-text-100 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-bg-300 text-text-100 text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-80 z-10">
                        <div className="text-xs font-medium mb-1">Sector Allocation Comparison</div>
                        <div className="text-xs">Shows the percentage allocation to each sector in the portfolio versus the benchmark. Overweight positions (portfolio greater than benchmark) indicate active sector bets, while underweight positions may reflect risk management or conviction-based decisions.</div>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-bg-300"></div>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-bg-300 pt-4">
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart 
                        data={sectorData} 
                        layout="vertical"
                        margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                        barCategoryGap={"30%"}
                      >
                        <CartesianGrid stroke="#2a2b3a" horizontal={false} />
                        <XAxis 
                          type="number" 
                          tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                          tick={{ fill: 'white', fontSize: 13, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400 }}
                          axisLine={false}
                          tickLine={false}
                          domain={[0, 'dataMax']}
                          ticks={[0, 0.05, 0.10, 0.15, 0.20, 0.25, 0.30, 0.35, 0.40]}
                        />
                        <YAxis 
                          type="category" 
                          dataKey="sector" 
                          tick={{ fill: 'white', fontSize: 13, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400 }}
                          width={110}
                          axisLine={false}
                          tickLine={false}
                          interval={0}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#292a37', border: 'none', fontSize: 13 }}
                          labelStyle={{ color: '#fff', fontSize: 13 }}
                          itemStyle={{ color: '#fff', fontSize: 13 }}
                          formatter={(value, name) => [`${(value * 100).toFixed(0)}%`, name]}
                        />
                        <Bar 
                          dataKey="ea_portfolio" 
                          fill="#2E5BFF" 
                          name="Portfolio"
                          radius={[0, 4, 4, 0]}
                          barSize={14}
                        />
                        <Bar 
                          dataKey="benchmark" 
                          fill="#00D4FF" 
                          name="Benchmark"
                          radius={[0, 4, 4, 0]}
                          barSize={14}
                        />
                        <Legend 
                          wrapperStyle={{ color: '#b0b8d1', fontSize: 13, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400, marginTop: 16 }}
                          verticalAlign="bottom"
                          align="center"
                          iconSize={14}
                          height={40}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-bg-200 shadow-lg rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <h2 className="text-xl font-semibold text-text-100">Factor Exposures</h2>
                    <div className="group relative">
                      <InformationCircleIcon className="h-5 w-5 text-text-300 hover:text-text-100 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-bg-300 text-text-100 text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-80 z-10">
                        <div className="text-xs font-medium mb-1">Factor Risk Exposures</div>
                        <div className="text-xs">Shows the portfolio's factor exposure compared to the benchmark. Both values are weighted averages of individual stock factor scores. Y-axis represents standardized factor exposure levels - higher values indicate stronger exposure to that factor. The seven factors are: Size (market cap), Value (earnings yield, sales yield, book yield), Growth (earnings and sales growth rates), Leverage (debt levels), Volatility (price volatility), Momentum (price trends), and Yield (dividend yield).</div>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-bg-300"></div>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-bg-300 pt-4">
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart 
                        data={factorData} 
                        margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                        barCategoryGap={"30%"}
                      >
                        <CartesianGrid stroke="#2a2b3a" />
                        <XAxis 
                          dataKey="factor" 
                          tickFormatter={(factor) => factor.charAt(0).toUpperCase() + factor.slice(1)} 
                          interval={0}
                          tick={{ fill: 'white', fontSize: 13, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis 
                          tickCount={5} 
                          domain={(() => {
                            if (factorData.length === 0) return [-3, 3];
                            
                            // Find min and max values across both portfolio and benchmark
                            const allValues = factorData.flatMap(item => [
                              item.ea_portfolio || 0, 
                              item.benchmark || 0
                            ]);
                            const minValue = Math.min(...allValues);
                            const maxValue = Math.max(...allValues);
                            
                            // Add 25% padding to the range
                            const range = Math.max(maxValue - minValue, 1); // Ensure minimum range of 1
                            const padding = range * 0.25;
                            
                            return [
                              Math.floor((minValue - padding) * 10) / 10, // Round to 1 decimal
                              Math.ceil((maxValue + padding) * 10) / 10
                            ];
                          })()}
                          tickFormatter={(value) => value.toFixed(1)}
                          tick={{ fill: 'white', fontSize: 13, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#292a37', border: 'none', fontSize: 13 }}
                          labelStyle={{ color: '#fff', fontSize: 13 }}
                          itemStyle={{ color: '#fff', fontSize: 13 }}
                          formatter={(value, name) => [value.toFixed(1), name]}
                          labelFormatter={(label) => `Factor: ${label.charAt(0).toUpperCase() + label.slice(1)}`}
                        />
                        <Legend 
                          wrapperStyle={{ color: '#b0b8d1', fontSize: 13, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400, marginTop: 16 }}
                          verticalAlign="bottom"
                          align="center"
                          iconSize={14}
                          height={40}
                        />
                        <Bar dataKey="ea_portfolio" fill="#2E5BFF" name="Portfolio" barSize={14} />
                        <Bar dataKey="benchmark" fill="#00D4FF" name="Benchmark" barSize={14} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              )}
            </div>
          ) : currentTab === 'Holdings' ? (
            <div className={`mx-auto ${showScores ? 'max-w-none px-0' : 'max-w-7xl px-4 sm:px-6 lg:px-8'}`}>
              {holdingsLoading ? (
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-text-100"></div>
                </div>
              ) : !holdingsData?.user_approved ? (
                <>
                  <div className="mx-auto max-w-2xl lg:mx-0">
                    <h2 className="text-5xl font-semibold tracking-tight text-text-100 sm:text-7xl">
                      Access Portfolio Holdings
                    </h2>
                    <p className="mt-8 text-pretty text-lg font-medium text-text-200 sm:text-xl/8">
                      {holdingsData?.message}
                    </p>
                  </div>
                                      <div className="hs-form-frame mt-4" data-region="na1" data-form-id="6e20eb60-2b8c-46ef-b4de-c2be840e2f12" data-portal-id="22063250"></div>
                </>
              ) : (
                <div>
                  <div className="sm:flex sm:items-center sm:justify-between">
                    <div className="sm:flex-auto">
                      <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-base font-semibold leading-6 text-text-100">
                          {model.model_type === 'etf'
                            ? 'ETF Holdings'
                            : holdingsData.is_latest_date === false ? 'Previous Holdings' : 'Current Holdings'} as of {holdingsData.portfolio_date}
                        </h1>
                        {model.model_type !== 'etf' && holdingsData.is_latest_date === false && (
                          <span className="inline-flex items-center rounded-full bg-yellow-400/10 px-3 py-1 text-xs font-medium text-yellow-300 border border-yellow-400/20">
                            Viewing Previous Holdings
                          </span>
                        )}
                      </div>
                      {holdingsData.previous_rebalance_date && (
                        <p className="mt-2 text-sm text-text-200">
                          Previous rebalance date: {holdingsData.previous_rebalance_date}
                        </p>
                      )}
                      {model.model_type !== 'etf' && holdingsData.is_latest_date === false && holdingsData.latest_rebalance_date && (
                        <p className="mt-2 text-sm text-text-200">
                          Current holdings are as of {holdingsData.latest_rebalance_date}.
                        </p>
                      )}
                      {model.model_type !== 'etf' && availableHoldingsDates.length > 0 && (
                        <p className="mt-1 text-xs text-text-200">
                          {availableHoldingsDates.length} rebalance dates available
                        </p>
                      )}
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-2 sm:mt-0">
                      <button
                        type="button"
                        onClick={() => setShowScores((prev) => !prev)}
                        className={`rounded-md px-3 py-2 text-sm font-semibold shadow-sm ${
                          showScores
                            ? 'bg-primary-100 text-white hover:bg-primary-200'
                            : 'bg-bg-200 text-text-100 hover:bg-bg-300'
                        }`}
                      >
                        {showScores ? 'Hide Scores' : 'Show Scores'}
                      </button>
                      {model.model_type !== 'etf' && (
                        <>
                          <button
                            type="button"
                            onClick={handlePreviousHoldingsDate}
                            disabled={!holdingsData.previous_rebalance_date}
                            className="rounded-md bg-bg-200 px-3 py-2 text-sm font-medium text-text-100 shadow-sm hover:bg-bg-300 disabled:cursor-not-allowed disabled:opacity-40"
                            title="Older rebalance"
                          >
                            &lt;
                          </button>
                          <span className="text-sm text-text-200">
                            {holdingsData.portfolio_date}
                          </span>
                          <button
                            type="button"
                            onClick={handleNextHoldingsDate}
                            disabled={!holdingsData.next_rebalance_date}
                            className="rounded-md bg-bg-200 px-3 py-2 text-sm font-medium text-text-100 shadow-sm hover:bg-bg-300 disabled:cursor-not-allowed disabled:opacity-40"
                            title="Newer rebalance"
                          >
                            &gt;
                          </button>
                          {holdingsData.is_latest_date === false && (
                            <button
                              type="button"
                              onClick={handleLatestHoldingsDate}
                              className="rounded-md bg-primary-100 px-3 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200"
                            >
                              Back to Current
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-8 space-y-8">
                    {/* Current Holdings Table - works for both stock and ETF models */}
                    {(holdingsData.current_holdings || holdingsData.holdings) && (
                      <div className="bg-bg-200/50 rounded-lg shadow p-4">
                        <div className="flex justify-between items-center mb-4 gap-3">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h2 className="text-base font-semibold leading-6 text-text-100">
                                {model.model_type !== 'etf' && holdingsData.is_latest_date === false ? 'Previous Holdings' : 'Current Holdings'}
                              </h2>
                              {model.model_type !== 'etf' && holdingsData.is_latest_date === false && (
                                <span className="inline-flex items-center rounded-full bg-yellow-400/10 px-2.5 py-0.5 text-xs font-medium text-yellow-300">
                                  Historical View
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-text-200">
                              {model.model_type !== 'etf' && holdingsData.is_latest_date === false
                                ? `Previous holdings as of ${holdingsData.portfolio_date}`
                                : `Current holdings as of ${holdingsData.portfolio_date}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {canDownloadHoldingsCSV && (
                              <button
                                onClick={downloadCurrentHoldingsCSV}
                                className="rounded-md bg-primary-100 px-3.5 py-2.5 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-100"
                              >
                                Download CSV
                              </button>
                            )}
                          </div>
                        </div>
                          <div className="overflow-x-auto">
                            <div className="max-h-[400px] overflow-y-auto">
                              <table className="min-w-full divide-y divide-bg-300">
                                <thead className="bg-bg-200 sticky top-0 z-10">
                                  <tr>
                                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-text-100">Ticker</th>
                                    {model.model_type !== 'etf' && (
                                      <>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-text-100">CUSIP</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-text-100">Security Name</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-text-100">Status</th>
                                      </>
                                    )}
                                    <th className="px-3 py-3.5 text-right text-sm font-semibold text-text-100">Weight (%)</th>
                                    {showScores && model.model_type !== 'etf' && (
                                      <>
                                        <th className="px-3 py-3.5 text-right text-sm font-semibold text-text-100">Signal</th>
                                        {SCORE_COLUMNS.map((col) => renderScoreHeader(col.key, col.label))}
                                      </>
                                    )}
                                    {showScores && model.model_type === 'etf' && (
                                      ETF_SCORE_COLUMNS.map((col) => (
                                        <th key={col.key} className="px-3 py-3.5 text-right text-sm font-semibold text-text-100">
                                          {col.label}
                                        </th>
                                      ))
                                    )}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-bg-300">
                                  {(holdingsData.current_holdings || holdingsData.holdings)
                                    .sort((a, b) => b.weight - a.weight)
                                    .map((holding, idx) => (
                                      <tr 
                                        key={holding.security_id || holding.ticker || idx}
                                        onClick={() => {
                                          if (model.model_type === 'etf') {
                                            handleETFClick(holding.ticker);
                                          } else if (holding.security_id) {
                                            handleSecurityClick(holding);
                                          }
                                        }}
                                        className="hover:bg-bg-300/50 cursor-pointer"
                                      >
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                                          {holding.ticker}
                                        </td>
                                        {model.model_type !== 'etf' && (
                                          <>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                                              {holding.cusip}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                                              {holding.name}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                                              <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                                                holding.is_active === false ? 'bg-yellow-400/10 text-yellow-300' : 'bg-green-400/10 text-green-400'
                                              }`}>
                                                {holding.security_status || 'Active'}
                                              </span>
                                              {holding.inactive_note && (
                                                <div className="mt-1 text-xs text-yellow-300">
                                                  {holding.inactive_note}
                                                </div>
                                              )}
                                            </td>
                                          </>
                                        )}
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-text-200">
                                          {parseFloat(holding.weight).toFixed(2)}%
                                        </td>
                                        {showScores && model.model_type !== 'etf' && (
                                          <>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-text-200">
                                              {renderSignalBadge(holding.signal_weight)}
                                            </td>
                                            {SCORE_COLUMNS.map((col) => (
                                              <td key={col.key} className="whitespace-nowrap px-3 py-4 text-sm text-right text-text-200">
                                                {formatHoldingScore(holding.component_scores?.[col.key])}
                                              </td>
                                            ))}
                                          </>
                                        )}
                                        {showScores && model.model_type === 'etf' && (
                                          ETF_SCORE_COLUMNS.map((col) => (
                                            <td key={col.key} className="whitespace-nowrap px-3 py-4 text-sm text-right text-text-200">
                                              {formatHoldingScore(holding[col.key])}
                                            </td>
                                          ))
                                        )}
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                      </div>
                    )}

                    {/* Recent Changes Table */}
                    {holdingsData.trades && holdingsData.trades.length > 0 && (
                      <div className="bg-bg-200/50 rounded-lg shadow p-4">
                        <div className="flex justify-between items-center mb-4 gap-3">
                          <div>
                            <h2 className="text-base font-semibold leading-6 text-text-100">Recent Changes</h2>
                            <p className="mt-1 text-sm text-text-200">
                              Changes from previous rebalance date: {holdingsData.previous_rebalance_date}
                            </p>
                            <div className="mt-2 flex space-x-4 text-sm">
                              <span className="text-green-400">
                                Buys: {holdingsData.trades.filter(trade => trade.action === 'Buy').length}
                              </span>
                              <span className="text-red-400">
                                Sells: {holdingsData.trades.filter(trade => trade.action === 'Sell').length}
                              </span>
                              {holdingsData.trades.some(trade => trade.is_active === false) && (
                                <span className="text-yellow-300">
                                  Inactive: {holdingsData.trades.filter(trade => trade.is_active === false).length}
                                </span>
                              )}
                              <span className="text-text-200">
                                Holds: {holdingsData.trades.filter(trade => trade.action === 'Hold').length}
                              </span>
                            </div>
                          </div>
                          {canDownloadHoldingsCSV && (
                            <button
                              onClick={downloadTradesCSV}
                              className="rounded-md bg-primary-100 px-3.5 py-2.5 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-100 shrink-0"
                            >
                              Download CSV
                            </button>
                          )}
                        </div>
                        <div className="overflow-x-auto">
                          <div className="max-h-[400px] overflow-y-auto">
                            <table className="min-w-full divide-y divide-bg-300">
                              <thead className="bg-bg-200 sticky top-0 z-10">
                                <tr>
                                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-text-100">Ticker</th>
                                  {model.model_type !== 'etf' && (
                                    <>
                                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-text-100">CUSIP</th>
                                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-text-100">Security Name</th>
                                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-text-100">Status</th>
                                    </>
                                  )}
                                  <th className="px-3 py-3.5 text-center text-sm font-semibold text-text-100">Action</th>
                                  <th className="px-3 py-3.5 text-right text-sm font-semibold text-text-100">Current Weight</th>
                                  <th className="px-3 py-3.5 text-right text-sm font-semibold text-text-100">Previous Weight</th>
                                  <th className="px-3 py-3.5 text-right text-sm font-semibold text-text-100">Weight Change</th>
                                  {showScores && model.model_type !== 'etf' && (
                                    <>
                                      <th className="px-3 py-3.5 text-right text-sm font-semibold text-text-100">Signal</th>
                                      {SCORE_COLUMNS.map((col) => renderScoreHeader(col.key, col.label))}
                                    </>
                                  )}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-bg-300">
                                {holdingsData.trades
                                  .sort((a, b) => Math.abs(parseFloat(b.percentage_change)) - Math.abs(parseFloat(a.percentage_change)))
                                  .map((trade, idx) => (
                                    <tr key={trade.cusip || trade.ticker || idx}>
                                      <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                                        {trade.ticker}
                                      </td>
                                      {model.model_type !== 'etf' && (
                                        <>
                                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                                            {trade.cusip}
                                          </td>
                                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                                            <button
                                              onClick={() => handleSecurityClick(trade.security)}
                                              className="hover:text-primary-100 text-left"
                                            >
                                              {trade.name}
                                            </button>
                                            {trade.inactive_note && (
                                              <div className="mt-1 text-xs text-yellow-300">
                                                {trade.inactive_note}
                                              </div>
                                            )}
                                          </td>
                                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                                            <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                                              trade.is_active === false ? 'bg-yellow-400/10 text-yellow-300' : 'bg-green-400/10 text-green-400'
                                            }`}>
                                              {trade.security_status || 'Active'}
                                            </span>
                                          </td>
                                        </>
                                      )}
                                      <td className="whitespace-nowrap px-3 py-4 text-sm text-center text-text-200">
                                        <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium
                                          ${trade.action === 'Buy' ? 'bg-green-400/10 text-green-400' :
                                            trade.action === 'Sell' ? 'bg-red-400/10 text-red-400' :
                                              'bg-bg-300/10 text-text-200'}`}>
                                          {trade.action}
                                        </span>
                                      </td>
                                      <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-text-200">
                                        {parseFloat(trade.current_weight).toFixed(2)}%
                                      </td>
                                      <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-text-200">
                                        {parseFloat(trade.previous_weight).toFixed(2)}%
                                      </td>
                                      <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-text-200">
                                        {parseFloat(trade.percentage_change).toFixed(2)}%
                                      </td>
                                      {showScores && model.model_type !== 'etf' && (
                                        <>
                                          <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-text-200">
                                            {renderSignalBadge(trade.signal_weight)}
                                          </td>
                                          {SCORE_COLUMNS.map((col) => (
                                            <td key={col.key} className="whitespace-nowrap px-3 py-4 text-sm text-right text-text-200">
                                              {formatHoldingScore(trade.component_scores?.[col.key])}
                                            </td>
                                          ))}
                                        </>
                                      )}
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : currentTab === 'Methodology' ? (
            <div className="mx-auto max-w-5xl">
              {methodologySummary && (
                <div className="rounded-xl border border-primary-100/20 bg-primary-100/5 p-5 mb-6">
                  <div className="flex items-start gap-3">
                    <InformationCircleIcon className="h-5 w-5 text-primary-100 shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-primary-100 mb-1">Methodology</p>
                      <p className="text-sm text-text-200 leading-relaxed">{methodologySummary}</p>
                    </div>
                  </div>
                </div>
              )}
              {construction ? (
                <div className="space-y-6">
                  <div className="bg-bg-200 rounded-lg border border-bg-300 p-6">
                    <h3 className="text-lg font-semibold text-text-100 mb-4">Portfolio Construction</h3>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="rounded-lg bg-bg-100 p-4">
                        <DetailTermLabel termKey="method" label="Method" />
                        <dd className="mt-1 text-sm font-semibold text-text-100">{METHOD_LABELS[construction.method] || construction.method}</dd>
                      </div>
                      <div className="rounded-lg bg-bg-100 p-4">
                        <DetailTermLabel termKey="signal" label="Signal" />
                        <dd className="mt-1 text-sm font-semibold text-text-100">{construction.signal}</dd>
                      </div>
                      <div className="rounded-lg bg-bg-100 p-4">
                        <DetailTermLabel termKey="benchmark" label="Benchmark" />
                        <dd className="mt-1 text-sm font-semibold text-text-100">{construction.benchmark}</dd>
                      </div>
                      <div className="rounded-lg bg-bg-100 p-4">
                        <DetailTermLabel termKey="nstock" label="Target Holdings" />
                        <dd className="mt-1 text-sm font-semibold text-text-100">{construction.nstock ?? '—'}</dd>
                      </div>
                      <div className="rounded-lg bg-bg-100 p-4">
                        <DetailTermLabel termKey="rebalance" label="Rebalance" />
                        <dd className="mt-1 text-sm font-semibold text-text-100">Monthly</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="bg-bg-200 rounded-lg border border-bg-300 p-6">
                    <h3 className="text-lg font-semibold text-text-100 mb-4">Constraints</h3>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="rounded-lg bg-bg-100 p-4">
                        <DetailTermLabel termKey="max_pos" label="Max Position" />
                        <dd className="mt-1 text-sm font-semibold text-text-100">
                          {construction.max_pos != null ? `${construction.max_pos}%` : '—'}
                        </dd>
                      </div>
                      <div className="rounded-lg bg-bg-100 p-4">
                        <DetailTermLabel termKey="bnd_sec" label="Sector vs Benchmark" />
                        <dd className="mt-1 text-sm font-semibold text-text-100">
                          {construction.bnd_sec != null ? `±${construction.bnd_sec}%` : '—'}
                        </dd>
                      </div>
                      <div className="rounded-lg bg-bg-100 p-4">
                        <DetailTermLabel termKey="bnd_adv" label="Volume Limit" />
                        <dd className="mt-1 text-sm font-semibold text-text-100">
                          {construction.bnd_adv != null ? `${construction.bnd_adv}% of ADV` : '—'}
                        </dd>
                      </div>
                      <div className="rounded-lg bg-bg-100 p-4">
                        <DetailTermLabel termKey="bnd_turnover" label="Turnover Limit" />
                        <dd className="mt-1 text-sm font-semibold text-text-100">
                          {construction.bnd_turnover != null ? `${construction.bnd_turnover}%` : '—'}
                        </dd>
                      </div>
                      <div className="rounded-lg bg-bg-100 p-4">
                        <DetailTermLabel termKey="bnd_factor" label="Factor Bound" />
                        <dd className="mt-1 text-sm font-semibold text-text-100">
                          {construction.bnd_factor != null ? `±${(construction.bnd_factor / 100).toFixed(1)}σ` : '—'}
                        </dd>
                      </div>
                      <div className="rounded-lg bg-bg-100 p-4">
                        <DetailTermLabel termKey="bnd_rel_weight" label="Relative Weight" />
                        <dd className="mt-1 text-sm font-semibold text-text-100">
                          {construction.bnd_rel_weight != null ? `±${construction.bnd_rel_weight}%` : '—'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              ) : (
                <div className="bg-bg-200 rounded-lg border border-bg-300 p-6">
                  <p className="text-sm text-text-200">
                    {model.model_type === 'etf'
                      ? 'ETF rotation models select top-ranked sector ETFs monthly using a multi-factor blend. See the methodology panel above for strategy details.'
                      : 'Detailed construction parameters are not available for this model.'}
                  </p>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
      <br></br>
      <DisclaimerFooter small />
      <PdfExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onExport={generateCustomPDF}
        whitelabelSettings={getWhitelabelSettings(userRecord)}
        title="Export Model Factsheet"
      />
      {/* Hidden light-themed chart for PDF export */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }} ref={chartRef}>
        <AreaChart
          width={2500}
          height={1200}
          data={performanceData}
          margin={{ top: 120, right: 100, left: 200, bottom: 180 }}
        >
          <CartesianGrid stroke="#eee" />
          <XAxis
            dataKey="year"
            stroke="#333"
            tick={{
              fill: '#333',
              fontSize: 60,
              fontFamily: 'Inter, Arial, sans-serif',
              fontWeight: 600,
            }}
            height={120}
            interval={0}
            label={{
              value: 'Year',
              position: 'insideBottom',
              offset: -40,
              style: { fill: '#333', fontSize: 70, fontWeight: 700, fontFamily: 'Inter, Arial, sans-serif' }
            }}
          />
          <YAxis
            stroke="#333"
            tick={{
              fill: '#333',
              fontSize: 60,
              fontFamily: 'Inter, Arial, sans-serif',
              fontWeight: 600,
            }}
            tickFormatter={v => `$${(v / 1000).toFixed(0)}K`}
            width={180}
            label={{
              value: 'Value ($)',
              angle: -90,
              position: 'insideLeft',
              offset: 0,
              style: { fill: '#333', fontSize: 70, fontWeight: 700, fontFamily: 'Inter, Arial, sans-serif' }
            }}
          />
          <Tooltip />
          <Legend layout="horizontal" verticalAlign="top" align="center" iconSize={120} wrapperStyle={{ fontSize: 120 }} />
          <Area
            type="monotone"
            dataKey="portfolio_10k_growth"
            stroke="#2E5BFF"
            fill="#eaf1ff"
            name="Portfolio Growth"
            strokeWidth={12}
          />
          <Area
            type="monotone"
            dataKey="benchmark_10k_growth"
            stroke="#00D4FF"
            fill="#eafffa"
            name="Benchmark Growth"
            strokeWidth={12}
          />
        </AreaChart>
      </div>
      {/* Hidden Chart.js canvas for PDF export */}
      <canvas ref={chartJsRef} width={2400} height={1200} style={{ display: 'none' }} />
    </div>
  );
}
